import { SITE_URL } from "utils/constant";
import { safeJsonLd } from "utils/safeHtml";
import { get, map } from "utils/lodash";
import helper from "utils/helper";
import { richTextToPlain } from "utils/lexicalFormatter";
import { format24h } from "utils/dateFormatter";
import { formatPrice } from "utils/currencyFormatter";

const StructuredData = ({
  type = "BeautySalon",
  shopData,
  reviews,
  totalReviews,
  locale = "vi",
}: {
  type?: string;
  shopData: unknown;
  reviews: unknown[];
  totalReviews: number;
  /** JSON-LD không dùng được useT() (client hook) — truyền locale từ server component cha */
  locale?: "vi" | "en";
}) => {
  const pageSlug = get(shopData, "slug", "");
  const baseUrl = SITE_URL;
  const link = `${baseUrl}${locale === "en" ? "/en" : ""}/${pageSlug}`;
  const shopName = get(shopData, "name", "");
  const bookingDayOfWeek = get(shopData, "bookingDayOfWeek", []);

  // Breadcrumb: Trang chủ > [Tên shop] — giúp Google hiển thị breadcrumb trên SERP
  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: locale === "en" ? "Home" : "Trang chủ",
        item: locale === "en" ? `${baseUrl}/en` : baseUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: shopName,
        item: link,
      },
    ],
  };

  const salonData = {
    "@type": type,
    "@id": link,

    sameAs: [link],
    name: shopName,
    description: richTextToPlain(get(shopData, "description")),
    url: link,
    image: helper.imageUrl(get(shopData, "avatar.url")),

    address:
      get(shopData, "displayedAddress") ??
      get(shopData, "oldStreetAddress") ??
      "",

    telephone: get(shopData, "phone", ""),

    // Geo coordinates
    geo: {
      "@type": "GeoCoordinates",
      latitude: get(shopData, "location.latitude", ""),
      longitude: get(shopData, "location.longitude", ""),
    },

    // Rating: chỉ khai báo khi có điểm thật + có review — rating 0/count 0
    // là structured data không hợp lệ (Google Rich Results báo lỗi).
    ...(Number(get(shopData, "rating")) > 0 && Number(totalReviews) > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            bestRating: 10,
            worstRating: 1,
            ratingValue: Number(Number(get(shopData, "rating")).toFixed(1)),
            ratingCount: Number(totalReviews),
          },
        }
      : {}),

    ...(get(shopData, "minPrice", "") && get(shopData, "maxPrice", "")
      ? {
          priceRange: `${formatPrice(
            get(shopData, "minPrice", "")
          )} - ${formatPrice(get(shopData, "maxPrice", ""))}`,
        }
      : {}),
    ...(reviews?.length > 0
      ? {
          review: map(reviews, (review) => ({
            "@type": "Review",
            author: {
              "@type": "Person",
              name: get(review, "user.name", "Vô danh"),
            },
            reviewRating: {
              "@type": "Rating",
              ratingValue: get(review, "rating") || undefined,
              bestRating: 10,
            },
            reviewBody: get(review, "comment", ""),
            datePublished: get(review, "createdAt", ""),
          })),
        }
      : {}),
    ...(bookingDayOfWeek?.length > 0
      ? {
          openingHoursSpecification: map(
            bookingDayOfWeek,
            (booking: {
              dayOfWeek: string;
              startTime: string;
              endTime: string;
            }) => ({
              "@type": "OpeningHoursSpecification",
              dayOfWeek: booking?.dayOfWeek,
              opens: format24h(booking?.startTime),
              closes: format24h(booking?.endTime),
            })
          ),
        }
      : {}),
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [salonData, breadcrumb],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: safeJsonLd(structuredData),
      }}
    />
  );
};
export default StructuredData;
